/**
 * Renders a glyph from ./glyphs/<name>.js by name. Each glyph is its own
 * module (raw SVG path markup, injected via dangerouslySetInnerHTML — safe
 * here since it's our own static build-time asset, never user input), so
 * webpack code-splits it into its own chunk and only the icons actually
 * rendered get fetched — see scripts/split-icon-data.js for how these
 * modules are generated from the design handoff's icon-data.js.
 *
 * @param {string} name - one of ./glyphs/names.js
 * @param {number=20} size
 * @param {string=} title - if set, icon is announced (role="img" aria-label);
 *   otherwise it's treated as decorative (aria-hidden)
 */
import React, { useEffect, useState } from "react";

// Module-level cache: a glyph fetched once (by any <Icon>) is reused
// synchronously by every subsequent render of the same name.
const glyphCache = new Map();
const glyphPromises = new Map();
// process.env.WIDGETS_URL is the CDN origin our static assets are mirrored
// on (e.g. https://cdn.my.revisual.io) — see InstallTab/helpers.js for the
// same env var used to build widget script URLs. Icon glyph chunks are
// requested by the dozens on first paint, so route them through the CDN
// rather than the app origin; __webpack_public_path__ is read synchronously
// by each import() call, so swapping it right before and restoring it right
// after only affects this one chunk request.
function loadGlyph(name) {
  if (!glyphPromises.has(name)) {
    const previousPublicPath = __webpack_public_path__;
    if (process.env.NODE_ENV === "production" && process.env.WIDGETS_URL) {
      // chunkFilename already contributes the "static/js/" segment, so the
      // override here is just the CDN's app root (mirrors client.min.js
      // living at "<WIDGETS_URL>/widgets/client.min.js").
      __webpack_public_path__ = `${process.env.WIDGETS_URL}/widgets/`;
    }
    const glyphImport = import(
      /* webpackChunkName: "icon-[request]" */ `./glyphs/${name}.js`
    );
    __webpack_public_path__ = previousPublicPath;

    glyphPromises.set(
      name,
      glyphImport
        .then((mod) => {
          glyphCache.set(name, mod.default);
          return mod.default;
        })
        .catch(() => {
          if (process.env.NODE_ENV !== "production") {
            // eslint-disable-next-line no-console
            console.warn(`Icon: unknown icon name "${name}"`);
          }
          glyphCache.set(name, null);
          return null;
        })
    );
  }
  return glyphPromises.get(name);
}

const Icon = ({ name, size = 20, title, className, ...rest }) => {
  const [glyph, setGlyph] = useState(() => glyphCache.get(name) ?? null);

  useEffect(() => {
    if (glyphCache.has(name)) {
      setGlyph(glyphCache.get(name));
      return;
    }
    let cancelled = false;
    loadGlyph(name).then((data) => {
      if (!cancelled) setGlyph(data);
    });
    return () => {
      cancelled = true;
    };
  }, [name]);

  if (!glyph) {
    // Reserves layout space while the chunk loads (or if the name is unknown).
    return (
      <svg
        width={size}
        height={size}
        className={className}
        aria-hidden="true"
        focusable="false"
        {...rest}
      />
    );
  }

  return (
    <svg
      viewBox={glyph.viewBox}
      width={size}
      height={size}
      className={className}
      role={title ? "img" : undefined}
      aria-label={title}
      aria-hidden={title ? undefined : true}
      focusable="false"
      dangerouslySetInnerHTML={{ __html: glyph.body }}
      {...rest}
    />
  );
};

export default Icon;
